import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Edit, DragIndicator, Visibility } from "@mui/icons-material";
import { CacheKey } from "../../../types/cache-keys";
import {
  validId,
} from "../../../hooks/base-hooks";
import GtmSolutionUseCaseComponentEditorDialog from "./GtmSolutionUseCaseComponentEditorDialog";
import ResourceRelationPanel from "../Panels/ResourceRelationPanel";
import TagManagementSection from "../Common/TagManagementSection";
import SnackableCourseButton from '../SnackableCourse/SnackableCourseButton';
import SnackableCourseManager from '../SnackableCourse/SnackableCourseManager';
import {
  useAddGtmSolutionUseCaseMutation,
  useDeleteGtmSolutionUseCaseComponentMutation,
  useFetchSingleGtmArchitecture,
  useFetchSingleGtmSolutionUseCase,
  useFetchSolutionUseCaseComponents,
  useUpdateGtmSolutionUseCaseMutation
} from "../../../hooks/gtm-solution-hooks";
import { useFetchCiscoProducts } from "../../../hooks/product-hooks";
import { asArray } from "../../../support/utils";
import { Product } from "../../../types/product";
import AdminConfirmDialog from "../Common/AdminConfirmDialog";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  gtmId: number;
  useCaseId?: number;
  onClose: () => void;
  onSaved: () => void;
  readOnly?: boolean;
};

export default function GtmSolutionUseCaseEditorDialog({
                                                         open,
                                                         mode,
                                                         gtmId,
                                                         useCaseId,
                                                         onClose,
                                                         onSaved,
                                                         readOnly = false,
                                                       }: Props) {
  const isEdit = mode === "edit" && validId(useCaseId);

  const { data: ciscoProducts, isLoading: productsLoading } = useFetchCiscoProducts();
  const ciscoProductById = useMemo(() => {
    if (!ciscoProducts) return new Map<number, Product>();
    const m = new Map<Product['id'], Product>();
    for (const p of ciscoProducts) m.set(p.id, p);
    return m;
  }, [ciscoProducts]);

  const { data: architecture, isLoading: architectureLoading } = useFetchSingleGtmArchitecture(gtmId);
  const { data: useCaseData, isLoading: useCaseLoading } = useFetchSingleGtmSolutionUseCase(gtmId, useCaseId);

  const useCase = useMemo(() => {
    if (isEdit) {
      if (architectureLoading && useCaseLoading) return null;
      if (useCaseData) return useCaseData;
      const found = architecture?.use_case?.find((s: any) => s.id === useCaseId);
      return found ?? null;
    }
    return null;
  }, [isEdit, useCaseId, architecture, architectureLoading, useCaseData, useCaseLoading]);

  const { mutateAsync: addUseCase, isPending: addingUseCase } = useAddGtmSolutionUseCaseMutation(gtmId);
  const { mutateAsync: updateUseCase, isPending: updatingUseCase } = useUpdateGtmSolutionUseCaseMutation(gtmId);

  const [componentsInitialized, setComponentsInitialized] = useState(false);
  const { data: componentData, isLoading: componentsLoading } = useFetchSolutionUseCaseComponents(useCaseId);
  const components = useMemo(() => {
    setComponentsInitialized(false);
    if (componentsLoading) return [];
    const sorted = (componentData ?? []).sort((a: any, b: any) => a.order - b.order);
    setComponentsInitialized(true);
    return sorted;
  }, [componentData, componentsLoading]);

  const { mutateAsync: deleteUseCaseComponent, isPending: deletingComponent } =
    useDeleteGtmSolutionUseCaseComponentMutation(useCaseId, gtmId);

  const [form, setForm] = useState<any>({
    name: "",
    short_description: "",
    description: "",
    order: 0,
    go_to_market: gtmId,
  });
  const [initialForm, setInitialForm] = useState<any>({
    name: "",
    short_description: "",
    description: "",
    order: 0,
    go_to_market: gtmId,
  });

  const [componentToEdit, setComponentToEdit] = useState<{
    mode: "create" | "edit";
    id?: number;
  } | null>(null);

  const [courseManagerOpen, setCourseManagerOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (!isEdit || !useCase) {
      const emptyForm = {
        name: "",
        short_description: "",
        description: "",
        order: architecture?.use_case?.length ?? 0,
        go_to_market: gtmId,
      };
      setForm(emptyForm);
      setInitialForm(emptyForm);
      return;
    }

    const nextForm = {
      name: useCase?.name ?? "",
      short_description: useCase?.short_description ?? "",
      description: useCase?.description ?? "",
      order: useCase?.order ?? architecture?.use_case?.length ?? 0,
      go_to_market: gtmId,
    };

    setForm(nextForm);
    setInitialForm(nextForm);
  }, [open, mode, isEdit, useCase, gtmId, architecture]);

  const saving =
    addingUseCase ||
    updatingUseCase ||
    useCaseLoading;

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.name !== initialForm.name ||
      form.short_description !== initialForm.short_description ||
      form.description !== initialForm.description ||
      form.order !== initialForm.order
    );
  }, [form.name, initialForm.name, form.short_description, initialForm.short_description, form.description, initialForm.description, form.order, initialForm.order]);

  const resetAndClose = () => {
    setForm({
      name: "",
      short_description: "",
      description: "",
      order: 0,
      go_to_market: gtmId,
    });
    setInitialForm({
      name: "",
      short_description: "",
      description: "",
      order: 0,
      go_to_market: gtmId,
    });
    setConfirmCloseOpen(false);
    onClose();
  };

  const onCloseDialog = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  const handleSave = async () => {
    if (readOnly) {
      onCloseDialog();
      return;
    }

    if (!isEdit) {
      await addUseCase({ ...form, go_to_market: gtmId });
    } else if (isEdit && useCaseId) {
      await updateUseCase({ ...form, go_to_market: gtmId, id: useCaseId });
    }

    onSaved();
    resetAndClose();
  };

  const handleDeleteComponent = async (componentId: number) => {
    await deleteUseCaseComponent({ id: componentId });
  };

  return (
    <>
      <Dialog open={open} onClose={onCloseDialog} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {readOnly
                ? mode === "create"
                  ? "View Security Use Case"
                  : `View Use Case: ${form?.name || `#${useCaseId}`}`
                : mode === "create"
                  ? "Create Security Use Case"
                  : `Use Case: ${form?.name || `#${useCaseId}`}`}
            </Typography>

            {isEdit && (
              <SnackableCourseButton
                entityType="security_use_case"
                entityId={useCaseId}
                entityName={form.name}
                disabled={!isEdit}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {saving && (
            <Box display="flex" alignItems="center" gap={2} py={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading…</Typography>
            </Box>
          )}

          <Box display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
            <Box flex={1}>
              <TextField
                fullWidth
                margin="normal"
                label="Name"
                value={form.name ?? ""}
                onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, name: e.target.value }))}
                disabled={readOnly}
              />

              <TextField
                fullWidth
                margin="normal"
                label="Short Description"
                value={form.short_description ?? ""}
                onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, short_description: e.target.value }))}
                multiline
                minRows={2}
                disabled={readOnly}
              />

              <TextField
                fullWidth
                margin="normal"
                label="Description"
                value={form.description ?? ""}
                onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, description: e.target.value }))}
                multiline
                minRows={3}
                disabled={readOnly}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Order"
                type="number"
                value={form.order ?? 0}
                onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, order: Number(e.target.value) }))}
                disabled={readOnly}
              />
            </Box>

            <Box flex={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Components</Typography>

                {isEdit && !readOnly && (
                  <Button startIcon={<Add />} size="small" onClick={() => setComponentToEdit({ mode: "create" })}>
                    Add Component
                  </Button>
                )}
              </Box>

              {mode === "create"
                ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Save the use case first to manage components.
                    </Typography>
                  </Paper>
                )
                : componentsInitialized
                  ? (
                    <Paper variant="outlined" sx={{ maxHeight: 400, overflow: "auto" }}>
                      <List dense>
                        {components.map((comp: any) => (
                          <ListItem
                            key={comp.id}
                            disablePadding
                            secondaryAction={
                              <Box>
                                <Tooltip title={readOnly ? "View" : "Edit"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => setComponentToEdit({ mode: "edit", id: comp.id })}
                                  >
                                    {readOnly ? <Visibility fontSize="small" /> : <Edit fontSize="small" />}
                                  </IconButton>
                                </Tooltip>

                                {!readOnly && (
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => handleDeleteComponent(comp.id)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            }
                          >
                            <ListItemButton>
                              <DragIndicator sx={{ mr: 1, color: "text.secondary" }} />
                              <ListItemText
                                primary={comp.name || `Component ${comp.id}`}
                                secondary={`Order: ${comp.order} • ${asArray(comp.product_feature).length} features`}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}

                        {components.length === 0 && (
                          <ListItem>
                            <ListItemText secondary="No components yet." />
                          </ListItem>
                        )}
                      </List>
                    </Paper>
                  )
                  : (
                    <Box display="flex" alignItems="center" gap={2} py={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading…</Typography>
                    </Box>
                  )
              }
            </Box>
          </Box>

          {isEdit && (
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                All Features Across Components
              </Typography>

              {componentsInitialized
                ? (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {components.map((comp: any) =>
                      asArray(comp.product_feature).map((feature: any) => {
                        const pid = typeof feature?.product === "object" ? feature?.product?.id : feature?.product;
                        const product = pid ? ciscoProductById.get(pid) : null;

                        return (
                          <Chip
                            key={`${comp.id}-${feature.id}`}
                            label={`${feature?.name || feature.id}${product ? ` (${product.name})` : ""}`}
                            size="small"
                            title={`Component: ${comp.name}`}
                          />
                        );
                      })
                    )}

                    {components.every((c: any) => asArray(c.product_feature).length === 0) && (
                      <Typography variant="body2" color="text.secondary">
                        No features in any component yet.
                      </Typography>
                    )}
                  </Box>
                )
                : (
                  <Box display="flex" alignItems="center" gap={2} py={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Loading…</Typography>
                  </Box>
                )
              }
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {isEdit && (
            <Box mb={3}>
              <ResourceRelationPanel
                joinEndpoint="db/security-use-case-to-resource"
                joinCacheKey={CacheKey.GtmSolutionUseCaseToResource}
                parentField="security_use_case"
                parentId={useCaseId ?? null}
                title="Resources"
                showNameColumn={false}
                maxHeight={300}
                invalidateKeys={[CacheKey.GtmSolutionUseCase]}
                disabled={!isEdit}
                readOnly={readOnly}
              />
            </Box>
          )}

          <TagManagementSection
            parentId={useCaseId ?? null}
            parentType={CacheKey.GtmSolutionUseCase}
            disabled={!isEdit || readOnly}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onCloseDialog} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {componentToEdit && isEdit && (
        <GtmSolutionUseCaseComponentEditorDialog
          open={true}
          mode={componentToEdit.mode}
          gtmId={gtmId}
          useCaseId={useCaseId}
          componentId={componentToEdit.id}
          onClose={() => setComponentToEdit(null)}
          onSaved={async () => {
            setComponentToEdit(null);
          }}
          readOnly={readOnly}
        />
      )}

      {courseManagerOpen && (
        <SnackableCourseManager
          open={courseManagerOpen}
          entityType="security_use_case"
          entityId={useCaseId}
          onClose={() => setCourseManagerOpen(false)}
        />
      )}

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this security use case editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}